import { action, computed, observable, toJS } from 'mobx';

// Config
import { DocumentCategory } from '../models/configuration/documents/DocumentCategory';

import { ChartStore, DataStore, EntityGroupStore, FilterStore } from '.';
import { Stores } from '../models/Stores';

import { ShareClass, ShareClassDocument } from '../models/entity';
import {
    isNullOrEmpty,
    isNullOrUndefined,
} from '@kurtosys/ksys-app-template/dist/utils/typeChecks';
import { IQueryContext } from '@kurtosys/ksys-app-template/dist/models/query';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import { sortNumber, sortString } from '../utils';
import getDocumentGroupOrder from '../utils/getDocumentGroupOrder';
import { translate } from '../Components/Translate';
import { NO_DOCUMENTS_MESSAGE } from '../constants';
import { DisclaimerElement } from '../models/configuration/DisclaimerElement';
import { getDisclaimers } from '../utils/disclaimers';
import {
    IFundCenterContext,
    IAnalytics,
} from '@kurtosys/ksys-app-template/dist/models/helpers/AnalyticsHelper';
import { ConditionalDataConfig } from '../models/configuration/ConditionalDataConfig';
import normalizeQueryResults from '../utils/query/normalizeQueryResults';
import getConditionalData from '../utils/query/getConditionalData';
import getConditionalDataFlag from '../utils/query/getConditionalDataFlag';
import { performanceCheck as checkPerformance } from '../utils/performanceCheck';
import { models } from '@kurtosys/ksys-app-template';
import { isArrayLike } from '@kurtosys/ksys-app-template/dist/utils/isArrayLike';
import { DocumentSearchRequests } from '../models/config';

export class EntityStore {
    // The original entity from the row
    @observable.ref initialValue: ShareClass;
    // The Column Documents Grid Documents, where the key would be the column identifier
    @observable columnDocumentsGridDocuments: Map<
        string,
        Array<ShareClassDocument>
    > = new Map();
    // The dynamic entity that is fetched on expand of card
    @observable.ref workingValue: ShareClass;
    @observable gtmRowDataContext: any = {};
    @observable.ref shareClassDetailLoading: boolean = false;

    // Stores
    @observable.ref dataStore: DataStore;
    @observable.ref filterStore: FilterStore;
    @observable.ref entityGroupStore: EntityGroupStore;
    @observable.ref chartStores: Map<string, ChartStore>;

    @observable.ref private isExpanded: boolean = false;

    constructor(stores: Stores, initialValue: ShareClass) {
        this.dataStore = stores.data;
        this.filterStore = stores.filter;
        this.entityGroupStore = stores.entityGroup;
        this.initialValue = initialValue;
        this.isExpanded = this.globalExpandStatus;
    }

    addSelectedDocuments(documents) {
        const { defaultDocumentSelectionState } = this.dataStore.config;

        if (defaultDocumentSelectionState === 'selected') {
            for (const document of documents) {
                this.entityGroupStore.addSelectedDocument(
                    this.initialValue.clientCode,
                    document
                );
            }
        }
    }

    @action
    getShareClassDetail = async () => {
        // fetch fund card if expanded and it hasn't been fetched yet
        if (
            this.expanded &&
            this.initialValue &&
            (!this.workingValue ||
                this.workingValue.clientCode !== this.initialValue.clientCode)
        ) {
            this.shareClassDetailLoading = true;
            const shareClassDetail = await this.dataStore.getFundDetail(
                this.initialValue
            );

            // Combine shareclass (entity) documents with documents returned by searchDocuments requests
            const { documents = [], _originalDocuments } = shareClassDetail;
            const searchDocuments = await this.dataStore.getDocumentsDetail(
                this.initialValue
            );

            // Merge original list of documents instead of the filtered documents as they come
            // from the original shareClass and not the shareClassDetail
            const currentDocuments = _originalDocuments || documents;
            shareClassDetail.documents = Array.from(
                new Set(currentDocuments.concat(searchDocuments))
            );

            this.addSelectedDocuments(shareClassDetail.documents);

            this.workingValue = { ...shareClassDetail };
            const { disclaimer } = this.dataStore.stores;
            if (disclaimer && !this.shareClassDetailLoading) {
                disclaimer.loadFundCardDisclaimers(shareClassDetail);
            }
            this.chartStores = this.getEntityCharts(
                this.value,
                this.queryContext
            );
            this.shareClassDetailLoading = false;
        }
    };

    @action
    getColumnDocumentGridDocuments = async (
        clientCodeColumnId: string,
        documentSearchRequests: DocumentSearchRequests
    ) => {
        if (!this.columnDocumentsGridDocuments.has(clientCodeColumnId)) {
            const columnDocuments = await this.dataStore.getDocumentsDetail(
                this.initialValue,
                documentSearchRequests
            );
            this.columnDocumentsGridDocuments.set(
                clientCodeColumnId,
                columnDocuments
            );

            this.addSelectedDocuments(columnDocuments);
        }
    };

    // TODO: Look into updating interfaces from ShareClass to IEntityEntity
    @computed
    get value(): ShareClass {
        return this.workingValue || this.initialValue;
    }

    /**
     * queryContext is required to use IQueryOptions configurations
     * The entityType value can be either shareClassDetail or shareClass depending on whether the row has been expanded.
     * shareClassDetail should be used once it is defined since it may contain additional documents loaded when the row is expanded.
     *
     * @readonly
     */
    @computed
    get queryContext(): IQueryContext {
        const { entityType } = this.dataStore.config;
        return {
            entityByType: {
                [entityType]: this.value,
            },
            globalDisclaimers: toJS(this.dataStore.rawDisclaimers) as any,
        };
    }

    @computed
    get documents() {
        if (this.value?.documents) {
            return this.value.documents;
        }
        return [];
    }

    @computed
    get groupedDocuments() {
        const {
            documentPropertySearch,
            documentFilterSearch,
            documentFilterInferredSearch,
        } = this.dataStore;
        return this.groupAndSortDocuments(this.documents, {
            documentPropertySearch,
            documentFilterSearch,
            documentFilterInferredSearch,
        });
    }

    @computed
    get hasDocumentCategories() {
        const { rowConfig } = this.dataStore.config;
        const { documentCategories } = rowConfig;
        return documentCategories && documentCategories.length > 0;
    }

    @computed
    get documentCategories(): Array<{
        groupedDocuments: Array<any>;
        heading: string;
        showSingleDocument: boolean;
    }> {
        if (this.hasDocumentCategories) {
            const {
                documentPropertySearch,
                documentFilterSearch,
                documentFilterInferredSearch,
                culture,
                queryStore,
            } = this.dataStore;
            const { rowConfig, showSingleDocument } = this.dataStore.config;
            const { documentCategories } = rowConfig;
            const result = [];
            for (const documentCategory of documentCategories as Array<
                DocumentCategory
            >) {
                // showSingleDocument for category or default to show showSingleDocument from module config
                const {
                    showSingleDocument: showSingleDocumentForCategory = showSingleDocument,
                } = documentCategory;
                const documentCategoryQuery = {
                    heading: documentCategory.heading,
                    documentGroup: documentCategory.groupBy,
                };
                const documentData = queryStore.getQueryResults(
                    this.queryContext,
                    documentCategoryQuery,
                    culture
                );
                if (
                    documentData &&
                    !isNullOrEmpty(documentData.documentGroup)
                ) {
                    // Check if the query had a sort, so that we do not override that with the default sorting
                    const groupedDocuments = this.groupAndSortDocuments(
                        documentData.documentGroup,
                        {
                            documentPropertySearch,
                            documentFilterSearch,
                            documentFilterInferredSearch,
                        },
                        documentCategoryQuery.documentGroup.sort === undefined
                    );
                    // If no grouped documents, don't show empty headers.
                    // Mainly an issue when filtering and hiding unfiltered documents
                    if (groupedDocuments?.length > 0) {
                        result.push({
                            groupedDocuments,
                            heading: documentData.heading,
                            showSingleDocument: showSingleDocumentForCategory,
                        });
                    }
                }
            }
            return result;
        }
    }

    /**
     * @options passed to allow the computed to fire when filters change
     */
    @action
    groupAndSortDocuments = (
        documents: Array<ShareClassDocument>,
        options: {
            documentFilterSearch: Array<any>;
            documentFilterInferredSearch: Array<any>;
            documentPropertySearch: Array<any>;
        },
        useDefaultSort: boolean = true
    ) => {
        const {
            documentGroupMeta,
            documentGroupInferred,
            cultureAliases,
            documentGroupOrderMetaPropertySort,
            documentGroupOrderInferredPropertySort,
            documentUngroupedLabel,
            showSingleDocument,
        } = this.dataStore.config;
        let { documentGroupOrder } = this.dataStore.config;
        // get client codes to determine unique documents
        const clientCodes = documents.map(mapObj => mapObj.clientCode);
        // filter documents to bring back a unique list based on the clientCode
        const uniqueDocuments = documents.filter((obj, pos, arr) => {
            // indexOf returns the index of the first occurrence, using this logic,
            // we can filter out the duplicates that don't match the with the first occurrence therefore giving us a unique list.
            let includeDocument = clientCodes.indexOf(obj.clientCode) === pos;

            // Filters out the documents to hide from view
            includeDocument =
                includeDocument &&
                this.entityGroupStore.includeDocumentByFilter(obj, options);
            // Check if the document has a grouped property
            if (documentGroupInferred) {
                if (
                    obj.inferred &&
                    isNullOrUndefined(obj.inferred[documentGroupInferred])
                ) {
                    obj.inferred[documentGroupInferred] =
                        documentUngroupedLabel || 'Other';
                }
            } else if (obj.meta && !obj.meta[documentGroupMeta]) {
                // Check if ungroup config has been configured
                // Set the group to the configure text
                // Else default it to Other
                obj.meta[documentGroupMeta] = {
                    value: documentUngroupedLabel || 'Other',
                };
            }

            return includeDocument;
        });

        const groupedDotNotation = documentGroupInferred
            ? `inferred.${documentGroupInferred}`
            : `meta.${documentGroupMeta}.value`;
        const groupedDocuments: {
            [key: string]: Array<ShareClassDocument>;
        } = groupBy(uniqueDocuments, groupedDotNotation);

        if (
            isNullOrEmpty(documentGroupOrder) &&
            !isNullOrEmpty(documentGroupOrderMetaPropertySort)
        ) {
            documentGroupOrder = getDocumentGroupOrder(
                groupedDocuments,
                documentGroupOrderMetaPropertySort,
                'meta'
            );
        } else if (
            isNullOrEmpty(documentGroupOrder) &&
            !isNullOrEmpty(documentGroupOrderInferredPropertySort)
        ) {
            documentGroupOrder = getDocumentGroupOrder(
                groupedDocuments,
                documentGroupOrderInferredPropertySort,
                'inferred'
            );
        }

        return Object.keys(groupedDocuments)
            .sort(sortString)
            .map(documentGroupName => {
                const documentGroupNameIndex = documentGroupOrder.indexOf(
                    documentGroupName
                );
                return {
                    order:
                        documentGroupNameIndex > -1
                            ? documentGroupNameIndex
                            : documentGroupOrder.length,
                    documentGroupName,
                    showSingleDocument,
                    documents: useDefaultSort
                        ? groupedDocuments[documentGroupName].sort((a, b) =>
                              sortString(
                                  get(
                                      cultureAliases,
                                      a.cultureCode,
                                      a.cultureCode
                                  ),
                                  get(
                                      cultureAliases,
                                      b.cultureCode,
                                      b.cultureCode
                                  )
                              )
                          )
                        : groupedDocuments[documentGroupName],
                };
            })
            .sort((a, b) => sortNumber(a.order, b.order));
    };

    @computed
    get shareClassDownloads() {
        let result: Array<ShareClassDocument>;
        if (
            this.entityGroupStore.selectedDownloads?.has(this.value.clientCode)
        ) {
            const {
                documentPropertySearch,
                documentFilterSearch,
                documentFilterInferredSearch,
            } = this.dataStore;
            result = Array.from(
                this.entityGroupStore.selectedDownloads
                    .get(this.value.clientCode)
                    .values()
            )
                .filter(document =>
                    this.entityGroupStore.includeDocumentByFilter(document, {
                        documentPropertySearch,
                        documentFilterSearch,
                        documentFilterInferredSearch,
                    })
                ) // Added an additional filter as the Array.from would return an additional value with the key of `true`
                .filter(document => document?.title !== undefined);
        }

        return result;
    }

    documentSelected = (documentClientCode: string) => {
        let isSelected = false;
        if (this.entityGroupStore.selectedDownloads) {
            const documents = this.entityGroupStore.selectedDownloads.get(
                this.value.clientCode
            );
            if (documents) {
                isSelected = documents.has(documentClientCode);
            }
        }
        return isSelected;
    };

    @computed
    get downloadCount() {
        return this.shareClassDownloads?.length;
    }

    @computed
    get fundCardDisclaimers() {
        return this.disclaimerByType(
            this.dataStore.config.rowConfig.disclaimers
        );
    }

    @computed
    get noDocumentsDisclaimers() {
        const { translation: translationStore } = this.dataStore.stores;
        const { noDocumentsDisclaimers = [] } = this.dataStore.config.rowConfig;
        return noDocumentsDisclaimers.length > 0
            ? this.disclaimerByType(noDocumentsDisclaimers)
            : [translate(translationStore, NO_DOCUMENTS_MESSAGE)];
    }

    @action
    disclaimerByType(disclaimerTypes: Array<DisclaimerElement>) {
        const { disclaimers = {} } = this.value;
        return getDisclaimers(
            this.dataStore,
            disclaimerTypes,
            disclaimers,
            this.queryContext
        );
    }

    @computed
    get expanded() {
        return this.isExpanded;
    }

    @computed
    get disableRow(): boolean {
        const { dataStore } = this;
        const { rowConfig } = dataStore.config;
        if (rowConfig) {
            const {
                hideFilteredDocuments,
                disableEmptyRow,
                expander = { enabled: true },
            } = rowConfig;
            if (expander.enabled === false) {
                return true;
            }
            if (hideFilteredDocuments && disableEmptyRow) {
                return this.groupedDocuments?.length === 0;
            }
        }
        return false;
    }

    @action
    logDataLayer(event: string) {
        const { dataStore } = this;
        const gtmContext: IAnalytics<IFundCenterContext> = {
            event,
            eventType: 'userActioned',
            context: {
                rowType: 'detail',
                rowData: this.gtmRowDataContext,
            },
        };
        dataStore.analyticsHelper.logEvent(gtmContext);
    }

    @action
    toggleExpanded = () => {
        const { value, dataStore } = this;
        dataStore.toggleExpandStatus(value as any);
        this.isExpanded = this.globalExpandStatus;
        if (this.expanded) {
            this.logDataLayer('table_row_expanded');
        }
    };

    @computed
    get globalExpandStatus() {
        const { value, dataStore } = this;
        return dataStore.getExpandStatus(value as any);
    }

    getEntityCharts(
        shareClass: ShareClass,
        queryContext: models.query.IQueryContext
    ) {
        const chartStores: Map<string, ChartStore> = new Map();
        const chartsConfig = this.dataStore.config.rowChartsConfig;
        if (chartsConfig && chartsConfig.length > 0) {
            chartsConfig.forEach(chartConfig => {
                chartStores.set(
                    chartConfig.id,
                    new ChartStore(
                        this.dataStore.stores,
                        chartConfig,
                        queryContext,
                        shareClass
                    )
                );
            });
        }
        return chartStores;
    }

    @computed
    get isShareclassDisabled() {
        if (this.entityGroupStore.rowExpanderConfig?.disabledFlag) {
            const conditionalConfig = this.entityGroupStore.rowExpanderConfig
                .disabledFlag as ConditionalDataConfig;
            const values = normalizeQueryResults({
                ...this.value.properties_pub,
                ...getConditionalData(this.value, conditionalConfig),
            });
            return getConditionalDataFlag(values, conditionalConfig);
        }
        return false;
    }

    @computed
    get isEntityDisabled(): boolean {
        const disabledEntityFlag = this.dataStore.config.disabledEntityFlag;

        if (
            disabledEntityFlag &&
            disabledEntityFlag.conditional &&
            isArrayLike(disabledEntityFlag.queries) &&
            disabledEntityFlag.queries.length > 0
        ) {
            const values = normalizeQueryResults({
                ...this.value.properties_pub,
                ...getConditionalData(this.value, disabledEntityFlag),
            });
            return getConditionalDataFlag(values, disabledEntityFlag);
        }
        return false;
    }

    @computed
    get hasPerformance() {
        const { dataStore, value } = this;
        const { config } = dataStore;
        return checkPerformance({
            dataStore,
            config,
            shareClass: value,
        });
    }

    @computed
    get watchListPropertyValue() {
        return get(
            this.value,
            `properties_pub.${this.dataStore.config.watchListProperty}.value`
        );
    }

    @action
    toggleWatchList = () => {
        this.dataStore.watchList.includes(this.watchListPropertyValue)
            ? this.removeWatchList()
            : this.addWatchList();
    };

    @action
    addWatchList = () => {
        const updatedWatchList = toJS(this.dataStore.watchList);
        updatedWatchList.push(this.watchListPropertyValue);
        this.dataStore.watchList = updatedWatchList;
        this.dataStore.analyticsHelper.logEvent({
            event: 'watchlist_add',
            eventType: 'click',
            context: {
                isin: this.watchListPropertyValue,
            },
        });
    };

    @action
    removeWatchList = () => {
        const updatedWatchList = toJS(this.dataStore.watchList);
        this.dataStore.watchList = updatedWatchList.filter(
            value => value !== this.watchListPropertyValue
        );
        this.dataStore.analyticsHelper.logEvent({
            event: 'watchlist_remove',
            eventType: 'click',
            context: {
                isin: this.watchListPropertyValue,
            },
        });
    };

    @computed
    get inWatchList() {
        return this.dataStore.watchList.includes(this.watchListPropertyValue);
    }
}



// WEBPACK FOOTER //
// ./src/stores/EntityStore.ts